import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, Search, RefreshCw, Mail, CheckCircle, XCircle, Clock, Trash2, Edit, UserPlus, X, Send, Plus, Infinity, Gift, UserCheck, ArrowRight, Eye, Ban, Globe } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/layout/DashboardLayout";

const COLORS = {
  principale: '#210875',
  accent: '#897FCB',
  bg: '#FDFCFF',
  success: '#059669',
  warning: '#f59e0b',
  error: '#dc2626',
};

export const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("users"); // "users", "referrals", "demo"
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState({});
  const [demoVisits, setDemoVisits] = useState([]);
  const [demoStats, setDemoStats] = useState({});
  const [excludedIps, setExcludedIps] = useState([]);
  const [newExcludedIp, setNewExcludedIp] = useState("");
  const api = useApi();

  useEffect(() => {
    loadUsers();
    loadReferrals();
    loadDemoVisits();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/users");
      setUsers(response.data.users || []);
      setStats(response.data.stats || {});
    } catch (err) {
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  };

  const loadReferrals = async () => {
    try {
      const response = await api.get("/admin/referrals");
      setReferrals(response.data.referrals || []);
      setReferralStats(response.data.stats || {});
    } catch (err) {
      console.error("Erreur chargement parrainages:", err);
    }
  };

  const loadDemoVisits = async () => {
    try {
      const [visitsRes, ipsRes] = await Promise.all([
        api.get("/admin/demo-visits"),
        api.get("/admin/excluded-ips")
      ]);
      setDemoVisits(visitsRes.data.visits || []);
      setDemoStats(visitsRes.data.stats || {});
      setExcludedIps(ipsRes.data.ips || []);
    } catch (err) {
      console.error("Erreur chargement visites démo:", err);
    }
  };

  const handleAddExcludedIp = async () => {
    if (!newExcludedIp.trim()) return;
    try {
      await api.post("/admin/excluded-ips", { ip: newExcludedIp.trim() });
      toast.success(`IP ${newExcludedIp} exclue`);
      setNewExcludedIp("");
      loadDemoVisits();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleRemoveExcludedIp = async (ip) => {
    try {
      await api.delete(`/admin/excluded-ips/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} retirée`);
      loadDemoVisits();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleActivateUser = async (userId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${userId}/activate`);
      toast.success("Abonnement activé pour 1 an !");
      loadUsers();
    } catch (err) {
      toast.error("Erreur lors de l'activation");
    }
    setActionLoading(false);
  };

  const handleActivateLifetime = async (userId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${userId}/activate-lifetime`);
      toast.success("Abonnement à vie activé !");
      loadUsers();
    } catch (err) {
      toast.error("Erreur lors de l'activation");
    }
    setActionLoading(false);
  };

  const handleSendWelcomeEmail = async (userId, email) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${userId}/send-welcome-email`);
      toast.success(`Email de bienvenue envoyé à ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setActionLoading(false);
  };

  const handleSendReferralEmail = async (userId, email) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${userId}/send-referral-email`);
      toast.success(`Email de parrainage envoyé à ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setActionLoading(false);
  };

  const handleConvertReferral = async (refereeId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/referrals/${refereeId}/convert`);
      toast.success("Parrainage converti, parrain crédité !");
      loadReferrals();
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la conversion");
    }
    setActionLoading(false);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/users/${selectedUser.id}`);
      toast.success("Utilisateur supprimé");
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
    setActionLoading(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const formData = new FormData(e.target);
      const createData = {
        email: formData.get("email"),
        password: formData.get("password"),
        prenom: formData.get("prenom"),
        nom: formData.get("nom"),
        subscription_plan: formData.get("subscription_plan"),
        subscription_days: parseInt(formData.get("subscription_days")),
        send_email: formData.get("send_email") === "on"
      };
      
      const response = await api.post("/admin/users/create", createData);
      toast.success(`Utilisateur ${createData.email} créé !${response.data.email_sent ? " Email envoyé." : ""}`);
      setShowCreateModal(false);
      e.target.reset();
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    }
    setActionLoading(false);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const formData = new FormData(e.target);
      const updateData = {
        prenom: formData.get("prenom"),
        nom: formData.get("nom"),
        subscription_status: formData.get("subscription_status"),
        subscription_plan: formData.get("subscription_plan"),
      };
      
      if (updateData.subscription_status === "active" && formData.get("duration")) {
        const days = parseInt(formData.get("duration"));
        const expDate = new Date();
        expDate.setDate(expDate.getDate() + days);
        updateData.subscription_expiration = expDate.toISOString();
      }
      
      await api.put(`/admin/users/${selectedUser.id}`, updateData);
      toast.success("Utilisateur mis à jour");
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    }
    setActionLoading(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"><CheckCircle size={12} /> Actif</span>;
      case "expired":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle size={12} /> Expiré</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"><Clock size={12} /> En attente</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.prenom?.toLowerCase().includes(search.toLowerCase()) ||
      u.nom?.toLowerCase().includes(search.toLowerCase());
    
    if (filter === "all") return matchesSearch;
    if (filter === "active") return matchesSearch && u.subscription_status === "active";
    if (filter === "expired") return matchesSearch && u.subscription_status === "expired";
    if (filter === "pending") return matchesSearch && (!u.subscription_status || u.subscription_status === "none");
    return matchesSearch;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: COLORS.bg }}>
              <Users size={24} style={{ color: COLORS.principale }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: COLORS.principale }}>Administration</h1>
              <p className="text-muted-foreground">Gestion des clients et parrainages</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors"
              style={{ backgroundColor: COLORS.success }}
            >
              <Plus size={16} /> Nouveau client
            </button>
            <button 
              onClick={() => { loadUsers(); loadReferrals(); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border hover:bg-muted transition-colors"
            >
              <RefreshCw size={16} /> Actualiser
            </button>
          </div>
        </div>

        {/* Onglets */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab("users")}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === "users" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Users size={16} className="inline mr-2" />
            Clients ({users.length})
          </button>
          <button
            onClick={() => setActiveTab("referrals")}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === "referrals" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Gift size={16} className="inline mr-2" />
            Parrainages ({referrals.length})
          </button>
          <button
            onClick={() => setActiveTab("demo")}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === "demo" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Eye size={16} className="inline mr-2" />
            Visites Démo ({demoStats.total || 0})
          </button>
          <button
            onClick={() => setActiveTab("campaigns")}
            data-testid="tab-campaigns"
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === "campaigns" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Send size={16} className="inline mr-2" />
            Campagnes
          </button>
        </div>

        {activeTab === "campaigns" && (
          <RgpdCampaignSection api={api} />
        )}

        {activeTab === "users" && (
          <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl border p-4">
            <div className="text-sm text-muted-foreground">Total clients</div>
            <div className="text-2xl font-bold" style={{ color: COLORS.principale }}>{stats.total || 0}</div>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="text-sm text-muted-foreground">Abonnements actifs</div>
            <div className="text-2xl font-bold" style={{ color: COLORS.success }}>{stats.active || 0}</div>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="text-sm text-muted-foreground">Expirés</div>
            <div className="text-2xl font-bold" style={{ color: COLORS.error }}>{stats.expired || 0}</div>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="text-sm text-muted-foreground">En attente paiement</div>
            <div className="text-2xl font-bold" style={{ color: COLORS.warning }}>{stats.pending || 0}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou email..."
              className="w-full h-11 pl-10 pr-4 rounded-lg border border-input bg-background"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-11 px-4 rounded-lg border border-input bg-background"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="expired">Expirés</option>
            <option value="pending">En attente</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-card rounded-xl border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="mx-auto mb-4 opacity-50" size={48} />
              <p>Aucun client trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Client</th>
                    <th className="text-left p-4 font-medium">Email</th>
                    <th className="text-left p-4 font-medium">Statut</th>
                    <th className="text-left p-4 font-medium">Plan</th>
                    <th className="text-left p-4 font-medium">Expiration</th>
                    <th className="text-left p-4 font-medium">Inscription</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div className="font-medium">{user.prenom} {user.nom}</div>
                      </td>
                      <td className="p-4">
                        <a href={`mailto:${user.email}`} className="flex items-center gap-1 text-sm hover:underline" style={{ color: COLORS.accent }}>
                          <Mail size={14} /> {user.email}
                        </a>
                      </td>
                      <td className="p-4">
                        {getStatusBadge(user.subscription_status)}
                      </td>
                      <td className="p-4">
                        <span className="text-sm">
                          {user.subscription_plan === "annual" ? "Annuel" : 
                           user.subscription_plan === "monthly" ? "Mensuel" : 
                           user.subscription_plan === "lifetime" ? "À vie" :
                           user.subscription_plan === "migration" ? "Migration" :
                           user.subscription_plan === "gratuit" ? "Gratuit" : "-"}
                        </span>
                      </td>
                      <td className="p-4 text-sm">
                        {formatDate(user.subscription_expiration)}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          {/* Activer 1 an */}
                          {user.subscription_status !== "active" && (
                            <button
                              onClick={() => handleActivateUser(user.id)}
                              disabled={actionLoading}
                              className="p-2 rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors"
                              title="Activer 1 an"
                            >
                              <UserPlus size={16} />
                            </button>
                          )}
                          {/* Activer à VIE */}
                          {user.subscription_plan !== "lifetime" && (
                            <button
                              onClick={() => handleActivateLifetime(user.id)}
                              disabled={actionLoading}
                              className="p-2 rounded-lg hover:bg-amber-100 text-amber-600 transition-colors"
                              title="Activer à VIE"
                            >
                              <Infinity size={16} />
                            </button>
                          )}
                          {/* Renvoyer email de bienvenue */}
                          <button
                            onClick={() => handleSendWelcomeEmail(user.id, user.email)}
                            disabled={actionLoading}
                            className="p-2 rounded-lg hover:bg-purple-100 text-purple-600 transition-colors"
                            title="Renvoyer email de bienvenue"
                          >
                            <Send size={16} />
                          </button>
                          {/* Envoyer email de parrainage */}
                          {user.subscription_status === "active" && (
                            <button
                              onClick={() => handleSendReferralEmail(user.id, user.email)}
                              disabled={actionLoading}
                              className="p-2 rounded-lg hover:bg-orange-100 text-orange-600 transition-colors"
                              title="Envoyer email parrainage"
                            >
                              <Gift size={16} />
                            </button>
                          )}
                          {/* Modifier */}
                          <button
                            onClick={() => { setSelectedUser(user); setShowEditModal(true); }}
                            className="p-2 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors"
                            title="Modifier"
                          >
                            <Edit size={16} />
                          </button>
                          {/* Supprimer */}
                          <button
                            onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }}
                            className="p-2 rounded-lg hover:bg-red-100 text-red-600 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}

        {/* Section Parrainages */}
        {activeTab === "referrals" && (
          <>
            {/* Stats Parrainages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Total parrainages</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.principale }}>{referralStats.total || 0}</div>
              </div>
              <div className="bg-card rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Convertis (payants)</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.success }}>{referralStats.converted || 0}</div>
              </div>
              <div className="bg-card rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">En attente</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.warning }}>{referralStats.pending || 0}</div>
              </div>
              <div className="bg-card rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Taux conversion</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.accent }}>{referralStats.conversion_rate || 0}%</div>
              </div>
            </div>

            {/* Table Parrainages */}
            <div className="bg-card rounded-xl border overflow-hidden">
              {referrals.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Gift className="mx-auto mb-4 opacity-50" size={48} />
                  <p>Aucun parrainage enregistré</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Parrain</th>
                        <th className="text-left p-4 font-medium"><ArrowRight size={14} className="inline" /></th>
                        <th className="text-left p-4 font-medium">Filleul(e)</th>
                        <th className="text-left p-4 font-medium">Date</th>
                        <th className="text-left p-4 font-medium">Statut</th>
                        <th className="text-left p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {referrals.map((ref, idx) => (
                        <tr key={idx} className="hover:bg-muted/30 transition-colors">
                          <td className="p-4">
                            <div className="font-medium" style={{ color: COLORS.principale }}>{ref.referrer_email}</div>
                            <div className="text-xs text-muted-foreground">Code: {ref.referrer_code}</div>
                          </td>
                          <td className="p-4 text-center">
                            <ArrowRight size={16} className="text-muted-foreground" />
                          </td>
                          <td className="p-4">
                            <div className="font-medium">{ref.referee_prenom || "-"}</div>
                            <div className="text-sm text-muted-foreground">{ref.referee_email}</div>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {formatDate(ref.created_at)}
                          </td>
                          <td className="p-4">
                            {ref.converted ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                <UserCheck size={12} /> Converti
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                <Clock size={12} /> En essai
                              </span>
                            )}
                          </td>
                          <td className="p-4">
                            {!ref.converted && (
                              <button
                                onClick={() => handleConvertReferral(ref.referee_id)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors"
                                style={{ backgroundColor: COLORS.success }}
                                title="Marquer comme converti et créditer le parrain"
                              >
                                Convertir
                              </button>
                            )}
                            {ref.converted && (
                              <span className="text-sm text-muted-foreground">
                                {formatDate(ref.conversion_date)}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Section Visites Démo */}
        {activeTab === "demo" && (
          <>
            {/* Stats Visites */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-card rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Total visites</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.principale }}>{demoStats.total || 0}</div>
              </div>
              <div className="bg-card rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Visiteurs uniques</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.accent }}>{demoStats.unique_ips || 0}</div>
              </div>
              <div className="bg-card rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Aujourd'hui</div>
                <div className="text-2xl font-bold" style={{ color: COLORS.success }}>{demoStats.today || 0}</div>
              </div>
            </div>

            {/* IPs Exclues */}
            <div className="bg-card rounded-xl border p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Ban size={18} /> IPs exclues du tracking (les tiennes)
              </h3>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newExcludedIp}
                  onChange={(e) => setNewExcludedIp(e.target.value)}
                  placeholder="Ex: 192.168.1.1"
                  className="flex-1 h-10 px-3 rounded-lg border border-input bg-background text-sm"
                />
                <button
                  onClick={handleAddExcludedIp}
                  className="px-4 h-10 rounded-lg text-white text-sm font-medium"
                  style={{ backgroundColor: COLORS.principale }}
                >
                  Exclure
                </button>
              </div>
              {excludedIps.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {excludedIps.map((ip, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted text-sm">
                      {ip}
                      <button onClick={() => handleRemoveExcludedIp(ip)} className="hover:text-red-500">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aucune IP exclue. Ajoute ton IP pour ne pas fausser les stats.</p>
              )}
            </div>

            {/* Table Visites */}
            <div className="bg-card rounded-xl border overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold">Dernières visites</h3>
                <button onClick={loadDemoVisits} className="text-sm text-muted-foreground hover:text-foreground">
                  <RefreshCw size={14} className="inline mr-1" /> Actualiser
                </button>
              </div>
              {demoVisits.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Eye className="mx-auto mb-4 opacity-50" size={48} />
                  <p>Aucune visite enregistrée</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 font-medium">Date</th>
                        <th className="text-left p-3 font-medium">IP</th>
                        <th className="text-left p-3 font-medium">Source</th>
                        <th className="text-left p-3 font-medium">Navigateur</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {demoVisits.slice(0, 100).map((visit, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="p-3 text-muted-foreground">
                            {new Date(visit.timestamp).toLocaleString('fr-FR')}
                          </td>
                          <td className="p-3 font-mono text-xs">{visit.ip}</td>
                          <td className="p-3 text-xs truncate max-w-[200px]" title={visit.referer}>
                            {visit.referer === 'direct' ? '🔗 Direct' : visit.referer?.replace(/https?:\/\//, '').split('/')[0]}
                          </td>
                          <td className="p-3 text-xs truncate max-w-[200px]" title={visit.user_agent}>
                            {visit.user_agent?.includes('Mobile') ? '📱' : '💻'} {visit.user_agent?.split(' ').slice(-2).join(' ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Modal Créer utilisateur */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card">
                <h3 className="text-lg font-semibold" style={{ color: COLORS.principale }}>Nouveau client</h3>
                <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-muted rounded">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateUser} className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Prénom *</label>
                    <input name="prenom" required className="w-full h-10 px-3 rounded-lg border border-input bg-background" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom *</label>
                    <input name="nom" required className="w-full h-10 px-3 rounded-lg border border-input bg-background" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input name="email" type="email" required className="w-full h-10 px-3 rounded-lg border border-input bg-background" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mot de passe *</label>
                  <input name="password" type="password" required minLength={6} className="w-full h-10 px-3 rounded-lg border border-input bg-background" />
                  <p className="text-xs text-muted-foreground mt-1">Minimum 6 caractères</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plan d'abonnement</label>
                  <select name="subscription_plan" defaultValue="annual" className="w-full h-10 px-3 rounded-lg border border-input bg-background">
                    <option value="monthly">Mensuel (12€)</option>
                    <option value="annual">Annuel (97€)</option>
                    <option value="lifetime">À vie</option>
                    <option value="migration">Migration (inclus)</option>
                    <option value="gratuit">Gratuit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Durée de l'abonnement</label>
                  <select name="subscription_days" defaultValue="365" className="w-full h-10 px-3 rounded-lg border border-input bg-background">
                    <option value="30">1 mois</option>
                    <option value="90">3 mois</option>
                    <option value="307">Jusqu'au 31/12/2026</option>
                    <option value="365">1 an</option>
                    <option value="730">2 ans</option>
                    <option value="3650">10 ans (lifetime)</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" name="send_email" id="send_email" defaultChecked className="w-4 h-4 rounded" />
                  <label htmlFor="send_email" className="text-sm">Envoyer l'email de bienvenue</label>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 h-10 rounded-lg border hover:bg-muted transition-colors">
                    Annuler
                  </button>
                  <button type="submit" disabled={actionLoading} className="flex-1 h-10 text-white rounded-lg font-medium transition-colors disabled:opacity-50" style={{ backgroundColor: COLORS.success }}>
                    {actionLoading ? "..." : "Créer le compte"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Modifier */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold" style={{ color: COLORS.principale }}>Modifier l'utilisateur</h3>
                <button onClick={() => { setShowEditModal(false); setSelectedUser(null); }} className="p-1 hover:bg-muted rounded">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleUpdateUser} className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Prénom</label>
                    <input name="prenom" defaultValue={selectedUser.prenom} className="w-full h-10 px-3 rounded-lg border border-input bg-background" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom</label>
                    <input name="nom" defaultValue={selectedUser.nom} className="w-full h-10 px-3 rounded-lg border border-input bg-background" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input value={selectedUser.email} disabled className="w-full h-10 px-3 rounded-lg border border-input bg-muted text-muted-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Statut abonnement</label>
                  <select name="subscription_status" defaultValue={selectedUser.subscription_status || ""} className="w-full h-10 px-3 rounded-lg border border-input bg-background">
                    <option value="">En attente</option>
                    <option value="active">Actif</option>
                    <option value="expired">Expiré</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plan</label>
                  <select name="subscription_plan" defaultValue={selectedUser.subscription_plan || ""} className="w-full h-10 px-3 rounded-lg border border-input bg-background">
                    <option value="">Aucun</option>
                    <option value="monthly">Mensuel (12€)</option>
                    <option value="annual">Annuel (97€)</option>
                    <option value="lifetime">À vie</option>
                    <option value="migration">Migration (inclus)</option>
                    <option value="gratuit">Gratuit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Durée (si activation)</label>
                  <select name="duration" defaultValue="365" className="w-full h-10 px-3 rounded-lg border border-input bg-background">
                    <option value="30">1 mois</option>
                    <option value="90">3 mois</option>
                    <option value="365">1 an</option>
                    <option value="3650">10 ans (lifetime)</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setShowEditModal(false); setSelectedUser(null); }} className="flex-1 h-10 rounded-lg border hover:bg-muted transition-colors">
                    Annuler
                  </button>
                  <button type="submit" disabled={actionLoading} className="flex-1 h-10 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
                    {actionLoading ? "..." : "Enregistrer"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Supprimer */}
        {showDeleteModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl w-full max-w-sm">
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 size={24} className="text-red-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Supprimer cet utilisateur ?</h3>
                <p className="text-muted-foreground text-sm mb-1">
                  <strong>{selectedUser.prenom} {selectedUser.nom}</strong>
                </p>
                <p className="text-muted-foreground text-sm mb-4">{selectedUser.email}</p>
                <p className="text-red-600 text-sm mb-6">
                  Cette action supprimera toutes ses données (ventes, dépenses, etc.) et est irréversible.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }} className="flex-1 h-10 rounded-lg border hover:bg-muted transition-colors">
                    Annuler
                  </button>
                  <button onClick={handleDeleteUser} disabled={actionLoading} className="flex-1 h-10 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50">
                    {actionLoading ? "..." : "Supprimer"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};


// ====================================================================
// Sous-composant : Campagne Email RGPD (broadcast aux abonnés actifs)
// ====================================================================
const RgpdCampaignSection = ({ api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/rgpd-email/recipients");
      setData(res.data);
    } catch {
      toast.error("Impossible de charger la liste des destinataires");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSend = async () => {
    if (!data || data.pending === 0) return;
    const ok = window.confirm(
      `Tu vas envoyer la campagne RGPD à ${data.pending} abonné(s) actif(s).\n\n` +
      `Sujet : ${data.subject}\n\n` +
      `OK pour confirmer l'envoi ?`
    );
    if (!ok) return;
    setSending(true);
    try {
      const res = await api.post("/admin/rgpd-email/send", { dry_run: false });
      setResult(res.data);
      toast.success(`✅ Envoyé à ${res.data.sent} abonné(s)`);
      await load(); // refresh: pending devient 0
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border p-6 text-center text-muted-foreground">
        <RefreshCw size={20} className="inline animate-spin mr-2" />
        Chargement…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="campaigns-section">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Abonnés ciblés" value={data.total} sublabel="hors démo & admin" color={COLORS.principale} />
        <StatCard label="À envoyer" value={data.pending} sublabel="jamais reçu" color={COLORS.warning} />
        <StatCard label="Déjà envoyés" value={data.already_sent} sublabel="historique" color={COLORS.success} />
      </div>

      {/* Card campagne */}
      <div className="bg-card rounded-xl border p-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <h3 className="font-bold text-lg" style={{ color: COLORS.principale }}>
              📨 Campagne RGPD — Tes données t'appartiennent
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Sujet : <strong>{data.subject}</strong>
            </p>
          </div>
          <button
            onClick={handleSend}
            disabled={sending || data.pending === 0}
            data-testid="rgpd-broadcast-btn"
            className="px-5 py-2.5 rounded-lg text-white font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{ backgroundColor: COLORS.principale }}
          >
            <Send size={16} />
            {sending
              ? "Envoi en cours…"
              : data.pending === 0
                ? "Aucun envoi en attente"
                : `Envoyer à ${data.pending} abonné${data.pending > 1 ? "s" : ""}`}
          </button>
        </div>

        {/* Aperçu HTML */}
        <details className="border rounded-lg p-3 bg-muted/30">
          <summary className="cursor-pointer font-medium text-sm">
            👁️ Voir l'aperçu de l'email
          </summary>
          <div
            className="mt-3 bg-white border rounded p-2 max-h-96 overflow-auto"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: data.preview_html }}
          />
        </details>

        {/* Liste destinataires */}
        <details className="border rounded-lg p-3 bg-muted/30 mt-3">
          <summary className="cursor-pointer font-medium text-sm">
            👥 Voir la liste des {data.total} destinataire{data.total > 1 ? "s" : ""}
          </summary>
          <div className="mt-3 max-h-72 overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="py-1.5 pr-2">Email</th>
                  <th className="py-1.5 pr-2">Prénom</th>
                  <th className="py-1.5 pr-2">Plan</th>
                  <th className="py-1.5">État</th>
                </tr>
              </thead>
              <tbody>
                {data.recipients.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 truncate max-w-xs">{r.email}</td>
                    <td className="py-1.5 pr-2">{r.prenom || "—"}</td>
                    <td className="py-1.5 pr-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                        {r.subscription_plan || "—"}
                      </span>
                    </td>
                    <td className="py-1.5">
                      {r.already_sent ? (
                        <span className="text-xs text-green-700 inline-flex items-center gap-1">
                          <CheckCircle size={12} /> envoyé
                        </span>
                      ) : (
                        <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                          <Clock size={12} /> en attente
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>

        {/* Résultat dernier envoi */}
        {result && (
          <div
            className="mt-4 p-3 rounded-lg border"
            style={{ backgroundColor: result.failed === 0 ? "#ECFDF5" : "#FEF3C7" }}
          >
            <p className="text-sm font-medium">
              ✅ Envoyés : {result.sent}
              {" · "}
              ❌ Échecs : {result.failed}
              {" · "}
              🎯 Cible : {result.total_targeted}
            </p>
            {result.failures && result.failures.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Échecs : {result.failures.join(", ")}
              </p>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
          ℹ️ Cible : tous les comptes <code>subscription_status = "active"</code> (mensuel, annuel,
          flash, lifetime, migration). Comptes <code>demo@biz360.fr</code> et admin exclus.
          Idempotent : un abonné déjà flagué ne sera pas relancé. Throttling 2s entre envois pour
          rester en règle avec Gmail SMTP.
        </p>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sublabel, color }) => (
  <div className="bg-card rounded-xl border p-4">
    <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className="text-3xl font-bold mt-1" style={{ color }}>{value}</div>
    {sublabel && <div className="text-xs text-muted-foreground mt-0.5">{sublabel}</div>}
  </div>
);

// Modal de confirmation rendu via portail manuel pour rester proche du composant
// (il sera affiché conditionnellement à l'intérieur de RgpdCampaignSection ci-dessus
//  via un mini composant inline plus bas si nécessaire — pour l'instant on garde une
//  confirmation native via window.confirm pour la simplicité)

